import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Separator } from '../../../components/ui/separator';
import { Lightbulb } from 'lucide-react';
import ObjectionPanel from './ObjectionPanel';

/**
 * Renders a step as one row per spoken line (question), each with its own
 * notes textarea underneath. All questions are visible and fillable in any
 * order. The active question (whichever note field is focused) drives the
 * copilot via onFocusQuestion.
 */
function ScriptPanel({
  step,
  notes,
  activeLineIdx,
  onNoteChange,
  onFocusQuestion,
  objectionBank,
  objectionsClicked,
  onToggleObjection,
}) {
  if (!step) {
    return (
      <div className="flex items-center justify-center h-full text-white/40 text-sm">
        Select a step to begin.
      </div>
    );
  }

  // Show only objections relevant to this step if it declares any; else show full bank.
  const triggerIds = step.objection_trigger_ids || [];
  const relevant = triggerIds.length
    ? (objectionBank || []).filter((o) => triggerIds.includes(o.id))
    : objectionBank || [];

  const stepNotes = notes || {};
  const spokenLines = step.spoken_lines || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-semibold text-white">{step.title}</h2>
        {step.annotation && (
          <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px]">NEPQ</Badge>
        )}
      </div>

      {step.annotation && (
        <div className="flex items-start gap-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-3 mb-4">
          <Lightbulb className="w-4 h-4 text-purple-300 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-purple-200/80 leading-snug">{step.annotation}</p>
        </div>
      )}

      <Separator className="bg-white/10 mb-3" />

      {/* One row per spoken line: the question, then its own note field. */}
      <div className="space-y-4 mb-3">
        {spokenLines.map((line, i) => {
          const isActive = activeLineIdx === i;
          return (
            <div
              key={i}
              className={`rounded-lg border p-3 transition-all ${
                isActive
                  ? 'border-[#00d4aa]/40 bg-[#00d4aa]/5'
                  : 'border-white/10 bg-black/20'
              }`}
            >
              <p className="text-white/80 text-sm leading-relaxed mb-2">
                &ldquo;{line}&rdquo;
              </p>
              <Textarea
                value={stepNotes[i] || ''}
                onChange={(e) => onNoteChange(step.id, i, e.target.value)}
                onFocus={() => onFocusQuestion(i)}
                placeholder="Their answer to this question..."
                className="min-h-[64px] bg-black/30 border-white/10 text-white/90 resize-y text-sm"
              />
            </div>
          );
        })}
      </div>

      {relevant.length > 0 && (
        <ObjectionPanel
          objections={relevant}
          objectionsClicked={objectionsClicked}
          onToggle={onToggleObjection}
        />
      )}
    </div>
  );
}

export default ScriptPanel;
